import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useMessaging } from '@/contexts/MessagingContext'
import { 
  TASKBAR_MODULES,
  TASKBAR_MODULES_CLIENT,
  TASKBAR_MODULES_ADMIN,
  TASKBAR_MODULES_PROFESSIONAL
} from '@/constants/landing/modules.constants'
import ModuleCard from './ModuleCard'
import type { TaskBarProps, Module } from '@/types/landing.types'

export default function TaskBar({ onModuleClick }: TaskBarProps) {
  const location = useLocation()
  const { user } = useAuth()
  
  // Get unread count from messaging context (safely handle if context not available)
  let unreadCount = 0
  try {
    const messaging = useMessaging()
    unreadCount = messaging?.unreadCount || 0
  } catch {
    // MessagingContext might not be available if user is not authenticated
    unreadCount = 0
  }

  // Déterminer le contexte basé sur la route et le rôle utilisateur
  const contextModules = useMemo(() => {
    const pathname = location.pathname
    const role = user?.role?.toLowerCase()

    let baseModules: readonly Module[]

    // Contexte CLIENT : soit route /client..., soit rôle client connecté
    if (pathname.startsWith('/client') || role === 'client') {
      baseModules = TASKBAR_MODULES_CLIENT
    }
    // Si on est sur une route admin (ou si l'utilisateur est admin)
    else if (pathname.startsWith('/admin') || role === 'admin') {
      baseModules = TASKBAR_MODULES_ADMIN
    }
    // Si on est sur une route professional (ou si l'utilisateur est professional)
    else if (pathname.startsWith('/professional') || role === 'professional') {
      baseModules = TASKBAR_MODULES_PROFESSIONAL
    }
    // Fallback : modules par défaut
    else {
      baseModules = TASKBAR_MODULES
    }

    // Inject unread count into message modules
    return baseModules.map(module => {
      if (module.id.startsWith('messages-')) {
        return { ...module, notifications: unreadCount }
      }
      return module
    })
  }, [location.pathname, user?.role, unreadCount])

  const handleModuleClick = (moduleId: string) => {
    if (onModuleClick) {
      onModuleClick(moduleId)
    }
  }

  return (
    <div className="flex-shrink-0">
      {/* TaskBar verticale avec effet glassmorphism */}
      <div className="
        bg-white/10 backdrop-blur-md border border-white/20
        rounded-2xl shadow-lg py-4 px-2 md:py-6 md:px-3
        flex flex-col items-center gap-3 md:gap-4
        transition-all duration-300 ease-out
        w-20 md:w-24
        h-fit
      ">
        {contextModules.map((module) => (
          <ModuleCard
            key={module.id}
            icon={module.icon}
            title={module.title}
            subtitle={module.subtitle}
            color={module.id}
            notifications={module.notifications}
            status={module.status}
            onClick={() => handleModuleClick(module.id)}
            className="w-full"
          />
        ))}
      </div>
    </div>
  )
}

